/**
 * Web player surface UI (artifacts/web/src/player/*).
 *
 * Note: the built-in signage widget strings live in the shared `player`
 * catalog (`player.widgets.*`, en/player.ts) — they are rendered by web,
 * mobile, and tv alike via `@workspace/player-shell/signage`.
 */
import { player } from "./player.ts";

export const playerUi = {
  // ── shared device selector (pages/device-selector.tsx) ──
  selector: {
    deviceStatus: {
      idle: "Idle",
      error: "Error",
    },
    unassignedLocation: "Unassigned",
    accountError: {
      title: "Couldn't load your account",
      description:
        "Something went wrong while loading your workspaces. Check your connection and try again.",
    },
    noWorkspaces: {
      title: "No workspaces",
      description:
        "Your account is not a member of any workspace yet. Ask an administrator to invite you before activating a screen.",
    },
    devicesError: {
      title: "Couldn't load devices",
      description:
        "Something went wrong while loading devices. Check your connection and try again.",
    },
    back: {
      workspaces: "Workspaces",
      locations: "Locations",
    },
    thisDevice: "{name} (this device)",
    empty: "No devices are available for you to activate here.",
  },

  // ── on-screen device picker (pages/device-picker.tsx) ──
  picker: {
    // Default label given to a newly claimed screen device.
    defaultDeviceLabel: "Screen",
    activateError: {
      title: "Could not activate device",
      description:
        "You may not have access to this device, or billing limits may apply.",
    },
    title: "Choose a screen",
    subtitle: "Select the device this screen should play as a kiosk player.",
  },

  // ── phone handoff (pages/handoff.tsx) ──
  handoff: {
    activateError: {
      title: "Could not activate screen",
      description:
        "The code may have expired, or you may not have access to this device.",
    },
    done: {
      title: "Screen activated",
      description: "The screen is starting now. Verify playback or close this page.",
    },
    controlNow: "Control this device now",
    actionLabel: "Activate",
    title: "Activate a screen",
    subtitle: "Choose the device this screen should play.",
  },

  // ── signed-out screen login (pages/player-login.tsx) ──
  login: {
    activateFromPhone: "Activate from your phone",
    activateFromPhoneHint:
      "Already signed in on your phone? Scan this code to pick a device and start this screen.",
    qrAlt: "Activation QR code",
    // The "or enter this code" copy is shared: player.pairing.* (en/player.ts).
  },

  // ── live player surface (pages/player.tsx) ──
  idle: {
    autoplayBlocked: {
      title: "Ready, press play",
      description: "Your browser blocked autoplay. Press play to start.",
    },
    channelDisabled: {
      title: "Music channel off",
      description: "Music is not enabled on this device.",
    },
    noPlayableTracks: {
      title: "No playable tracks",
      playlistNoAudio: '"{playlistName}" is scheduled but has no audio.',
      scheduledNoAudio: "The scheduled playlist has no audio.",
      // Sentence wraps an inline <code>R2_CATALOGUE_BASE</code> element.
      catalogueHintPre:
        "Check that catalogue tracks have been indexed and the API server has",
      catalogueHintPost: "set.",
    },
    nothingScheduled: {
      title: "Nothing scheduled",
      description: "This player is connected and waiting for a schedule.",
    },
  },
  autoplay: {
    tapToStart: "Tap to start",
    tapToStartAria: "Tap to start playback",
    hint: "Your browser requires a tap to play audio",
  },
  status: {
    onSchedule: "On schedule",
    manual: "Manual",
    // Aliases of the shared player catalog: the tv kiosk renders the same two
    // strings from `player.cacheStatus.*`, and one literal keeps the offline
    // copy identical on every surface.
    playingFromCache: player.cacheStatus.playingFromCache,
    offlinePlayingFromCache: player.cacheStatus.offlinePlayingFromCache,
    offlineReady: "Offline ready · {count} files cached",
    offlineCaching: "Caching for offline · {cached}/{total}",
    offlineReadinessTitle:
      "How much of the current schedule window's media is downloaded for offline playback",
  },
  connection: {
    live: "Live",
    connecting: "Connecting",
  },
  transport: {
    switchMode: "Switch Mode",
    switchModeAria: "Switch mode",
    signOut: "Sign Out",
  },

  // ── device runtime gate (player-runtime.tsx) ──
  signedOut: {
    title: "This screen was signed out",
    description:
      "Its credential is no longer valid. Sign in again to choose a device.",
    action: "Back to sign in",
  },

  // Cold boot with the server unreachable and no cached window yet (CB-160).
  unreachable: {
    title: "Can't reach OmniMedia",
    description:
      "This screen is set up, but the server can't be reached and nothing is cached yet. It will keep retrying automatically.",
    retry: "Try again",
    reset: "Reset this screen",
    resetConfirm:
      "Resetting signs this screen out and clears everything it has downloaded. Continue?",
  },

  // ── playback engine warnings surfaced to the operator (hooks/use-playback.tsx) ──
  playback: {
    audioSuspended: "AudioContext suspended. Tap the screen to enable sound",
    stalled: "Playback stalled: audio time not advancing",
    failed: "Playback failed: {message}",
    notReady: "Player not ready",
    remotePlayBlocked:
      "The player's browser blocked playback. Tap the player screen once to allow audio",
  },

  // ── client-release advisory pushed by the server over the realtime gateway
  // (player/components/client-advisory-notice.tsx). Advice about the build
  // this player runs; it never stops playback, so the copy stays informational
  // and the only action offered is a reload ──
  update: {
    recommended: "Update available",
    required: "Update needed",
    blocked: "Known problem in this build",
    featuresOff: "Some features are switched off",
    featureList: "Switched off: {features}",
    minVersion: "Needs {version} or newer.",
    reload: "Reload",
    reloadAria: "Reload this player to pick up the newest build",
    howToUpdate: "How to update",
  },

  // ── machine reason keys reported by players when a remote command was
  // dropped (command-result error field), translated on the console toast ──
  commandReason: {
    channelNotActive: "That channel is not playing right now.",
    nothingScheduled: "Nothing is scheduled to play on that channel right now.",
    noScreenMedia: "No video or signage is loaded on the screen.",
    takeoverActive: "An announcement takeover is playing. Try again when it ends.",
    liveAnnouncementActive:
      "A live announcement is on air. Playback controls resume when it ends.",
    emptyPlaylist: "That playlist has no playable items.",
    historyEmpty: "There is no previous track to go back to.",
    notHandled: "The player could not apply this command.",
  },
} as const;
